import React, { useCallback, useEffect, useState } from 'react';
import {
	Alert,
	Box,
	Chip,
	CircularProgress,
	FormControlLabel,
	Grid,
	IconButton,
	MenuItem,
	Pagination,
	Paper,
	Select,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useHistory } from 'react-router-dom';
import { ISort } from 'features/common/types';
import { IProviderPayableLineListFilters, IProviderPayableLineListItem } from 'features/salesOrder/types';
import { getProviderPayableLineList } from 'features/salesOrder/services';
import { ProviderPayablesFilters } from './ProviderPayablesFilters';
import routes from 'constants/routes';
import { openPaymentsUrl } from 'pages/SalesOrder/SalesOrderScreen/helpers';
import { extractErrorMessage } from '../../../quotation/helpers';
import {
	PROVIDER_PAYABLE_LINE_STATUS_COLOR,
	PROVIDER_PAYABLE_LINE_STATUS_LABEL,
	PROVIDER_PAYABLE_LINE_TYPE_LABEL,
} from '../../constants';

const PAGE_SIZE_OPTIONS = [50, 100, 150, 200];
const DEFAULT_PAGE_SIZE = 100;
const CRM_DEAL_URL = 'https://crm.mevuelo.com/dashboard/cases/';

const BooleanCell = ({ value }: { value?: boolean }) => {
	if (value === undefined || value === null)
		return (
			<Typography variant="caption" color="text.disabled">
				—
			</Typography>
		);
	return <Chip label={value ? 'Sí' : 'No'} color={value ? 'success' : 'error'} size="small" variant="outlined" />;
};

const CopyButton = ({ value }: { value: string }) => {
	const [copied, setCopied] = useState(false);
	const handleCopy = (e: React.MouseEvent) => {
		e.stopPropagation();
		navigator.clipboard.writeText(value).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		});
	};
	return (
		<Tooltip title={copied ? '¡Copiado!' : 'Copiar'}>
			<IconButton size="small" onClick={handleCopy} sx={{ ml: 0.5, p: 0.25 }}>
				<ContentCopyIcon sx={{ fontSize: 14, color: copied ? 'success.main' : 'action.active' }} />
			</IconButton>
		</Tooltip>
	);
};

interface ProviderPayablesListProps {
	fixedProviderId?: string;
}

const buildFixedProviderFilters = (fixedProviderId?: string): IProviderPayableLineListFilters =>
	fixedProviderId ? { providerIds: [fixedProviderId] } : {};

export const ProviderPayablesList = (
	{ fixedProviderId }: ProviderPayablesListProps = {} as ProviderPayablesListProps,
): JSX.Element => {
	const history = useHistory();

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [items, setItems] = useState<IProviderPayableLineListItem[]>([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [currentSize, setCurrentSize] = useState(DEFAULT_PAGE_SIZE);
	const [pageTotal, setPageTotal] = useState(0);
	const [totalCount, setTotalCount] = useState(0);
	const [sortFields] = useState<ISort[]>([{ field: 'created', order: 'desc' }]);
	const [filters, setFilters] = useState<IProviderPayableLineListFilters>(() =>
		buildFixedProviderFilters(fixedProviderId),
	);

	// Toggles para columnas de conciliación
	const [showReconciliation, setShowReconciliation] = useState(false);

	const loadData = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);
			const res = await getProviderPayableLineList(currentPage, currentSize, sortFields, filters);
			setItems(res.data.elements ?? []);
			setPageTotal(Math.ceil(res.data.total / currentSize));
			setTotalCount(res.data.total);
		} catch (error) {
			console.log(error);
			setError(extractErrorMessage(error, 'No se pudo cargar el listado. Intentá de nuevo.'));
		} finally {
			setLoading(false);
		}
	}, [currentPage, currentSize, sortFields, filters]);

	useEffect(() => {
		loadData();
	}, [loadData]);

	const mergeWithFixedProvider = useCallback(
		(newFilters: IProviderPayableLineListFilters): IProviderPayableLineListFilters => ({
			...newFilters,
			...(fixedProviderId ? { providerIds: [fixedProviderId] } : {}),
		}),
		[fixedProviderId],
	);

	const handleFiltersChange = useCallback(
		(newFilters: IProviderPayableLineListFilters) => {
			setFilters(mergeWithFixedProvider(newFilters));
			setCurrentPage(1);
		},
		[mergeWithFixedProvider],
	);

	const handleClearFilters = useCallback(() => {
		setFilters(buildFixedProviderFilters(fixedProviderId));
		setCurrentPage(1);
	}, [fixedProviderId]);

	const handleRowClick = (item: IProviderPayableLineListItem, event: React.MouseEvent) => {
		openPaymentsUrl(history, item.saleOrderId, item.id, event);
	};

	return (
		<Box>
			<ProviderPayablesFilters
				onFiltersChange={handleFiltersChange}
				onClearFilters={handleClearFilters}
				hideProviderFilter={!!fixedProviderId}
				embedded={!!fixedProviderId}
			/>

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			{loading ? (
				<Box display="flex" justifyContent="center" py={6}>
					<CircularProgress />
				</Box>
			) : (
				<>
					{/* Barra de herramientas */}
					<Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
						<Typography variant="body2" color="text.secondary">
							{totalCount} resultado{totalCount !== 1 ? 's' : ''}
						</Typography>
						<Tooltip title="Muestra columnas adicionales de conciliación (datos mockeados — pendiente backend)">
							<FormControlLabel
								control={
									<Switch
										checked={showReconciliation}
										onChange={(e) => setShowReconciliation(e.target.checked)}
										size="small"
										color="warning"
									/>
								}
								label={
									<Typography variant="caption" color="text.secondary">
										Ver columnas de conciliación
									</Typography>
								}
							/>
						</Tooltip>
					</Box>

					<TableContainer component={Paper} variant="outlined">
						<Table size="small">
							<TableHead>
								<TableRow sx={{ bgcolor: 'grey.50' }}>
									<TableCell>ID Línea</TableCell>
									<TableCell>Proveedor</TableCell>
									<TableCell>Tipo</TableCell>
									<TableCell>Ticket / Localizador</TableCell>
									<TableCell>Pasajero</TableCell>
									<TableCell>Orden de venta</TableCell>
									<TableCell>Deal</TableCell>
									<TableCell align="right">A pagar</TableCell>
									<TableCell align="right">Pagado</TableCell>
									<TableCell align="right">Pendiente</TableCell>
									<TableCell align="center">Estado</TableCell>
									{showReconciliation && (
										<>
											<TableCell align="center" sx={{ color: 'warning.main', whiteSpace: 'nowrap' }}>
												≥1 cobro cliente
											</TableCell>
											<TableCell align="center" sx={{ color: 'warning.main', whiteSpace: 'nowrap' }}>
												Saldo pendiente
											</TableCell>
											<TableCell align="center" sx={{ color: 'warning.main', whiteSpace: 'nowrap' }}>
												Cobro ≥ vuelos
											</TableCell>
										</>
									)}
								</TableRow>
							</TableHead>
							<TableBody>
								{items.length === 0 ? (
									<TableRow>
										<TableCell
											colSpan={showReconciliation ? 14 : 11}
											align="center"
											sx={{ py: 4, color: 'text.secondary' }}
										>
											No hay resultados para los filtros aplicados.
										</TableCell>
									</TableRow>
								) : (
									items.map((item) => (
										<TableRow
											key={item.id}
											hover
											onClick={(event) => handleRowClick(item, event)}
											sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'primary.50' } }}
										>
											{/* ID línea */}
											<TableCell>
												<Box display="flex" alignItems="center">
													<Typography variant="caption" fontFamily="monospace" color="text.secondary">
														{item.id.substring(0, 8)}…
													</Typography>
													<CopyButton value={item.id} />
												</Box>
											</TableCell>

											{/* Proveedor */}
											<TableCell>
												<Typography variant="body2">{item.providerName ?? item.providerId}</Typography>
											</TableCell>

											{/* Tipo */}
											<TableCell>
												<Typography variant="caption" color="text.secondary">
													{PROVIDER_PAYABLE_LINE_TYPE_LABEL[item.payableType]}
												</Typography>
											</TableCell>

											{/* Ticket / Localizador */}
											<TableCell>
												{item.ticket ? (
													<Typography variant="body2" fontFamily="monospace">
														{item.ticket}
													</Typography>
												) : (
													<Typography variant="body2" color="text.secondary">
														—
													</Typography>
												)}
											</TableCell>

											{/* Pasajero */}
											<TableCell>
												<Typography variant="body2">{item.passengerName ?? '—'}</Typography>
											</TableCell>

											{/* Orden de venta */}
											<TableCell>
												<Box display="flex" alignItems="center">
													<Tooltip title="Ir a la orden de venta">
														<Typography
															variant="body2"
															color="primary"
															sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
															onClick={(e) => {
																e.stopPropagation();
																history.push(routes.salesOrder.edit.replace(':id', item.saleOrderId));
															}}
														>
															{item.saleOrderId.substring(0, 8)}…
														</Typography>
													</Tooltip>
													<Tooltip title="Abrir en nueva pestaña">
														<IconButton
															size="small"
															sx={{ p: 0.25 }}
															onClick={(e) => {
																e.stopPropagation();
																window.open(
																	`${window.location.origin}${routes.salesOrder.edit.replace(':id', item.saleOrderId)}`,
																	'_blank',
																);
															}}
														>
															<OpenInNewIcon sx={{ fontSize: 14 }} />
														</IconButton>
													</Tooltip>
													<CopyButton value={item.saleOrderId} />
												</Box>
											</TableCell>

											{/* Deal */}
											<TableCell onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
												{!item.isBitrixDeal ? (
													<Box display="flex" alignItems="center">
														<Tooltip title="Abrir en CRM">
															<Typography
																variant="body2"
																color="primary"
																sx={{ cursor: 'pointer' }}
																onClick={(e) => {
																	e.stopPropagation();
																	window.open(`${CRM_DEAL_URL}${item.dealId}`, '_blank', 'noopener,noreferrer');
																}}
															>
																#{item.dealId}
															</Typography>
														</Tooltip>

														<Tooltip title="Abrir en CRM">
															<IconButton
																size="small"
																sx={{ p: 0.25 }}
																onClick={(e) => {
																	e.stopPropagation();
																	window.open(`${CRM_DEAL_URL}${item.dealId}`, '_blank', 'noopener,noreferrer');
																}}
															>
																<OpenInNewIcon sx={{ fontSize: 14 }} />
															</IconButton>
														</Tooltip>

														<Box onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
															<CopyButton value={String(item.dealId)} />
														</Box>
													</Box>
												) : (
													<Box display="flex" alignItems="center">
														<Tooltip title="Deal de Bitrix">
															<Typography variant="body2" color="primary" sx={{ cursor: 'default' }}>
																#{item.dealId}
															</Typography>
														</Tooltip>

														<Box onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
															<CopyButton value={String(item.dealId)} />
														</Box>
													</Box>
												)}
											</TableCell>

											{/* Montos */}
											<TableCell align="right">
												<Typography variant="body2">
													{item.currency} {item.payableAmount.toLocaleString('es-UY', { minimumFractionDigits: 2 })}
												</Typography>
											</TableCell>
											<TableCell align="right">
												<Typography
													variant="body2"
													color={item.totalPaid > 0 ? 'success.main' : 'text.secondary'}
													fontWeight={item.totalPaid > 0 ? 'bold' : 'normal'}
												>
													{item.currency} {item.totalPaid.toLocaleString('es-UY', { minimumFractionDigits: 2 })}
												</Typography>
											</TableCell>
											<TableCell align="right">
												<Typography
													variant="body2"
													color={item.pending > 0 ? 'warning.main' : item.pending < 0 ? 'error.main' : 'success.main'}
													fontWeight="bold"
												>
													{item.currency} {item.pending.toLocaleString('es-UY', { minimumFractionDigits: 2 })}
												</Typography>
											</TableCell>

											{/* Estado */}
											<TableCell align="center">
												<Chip
													label={PROVIDER_PAYABLE_LINE_STATUS_LABEL[item.paymentStatus]}
													color={PROVIDER_PAYABLE_LINE_STATUS_COLOR[item.paymentStatus]}
													size="small"
												/>
											</TableCell>

											{/* Columnas de conciliación (toggle) */}
											{showReconciliation && (
												<>
													<TableCell align="center">
														<BooleanCell value={item.hasAtLeastOneClientPayment} />
													</TableCell>
													<TableCell align="center">
														<BooleanCell value={item.hasClientPendingBalance} />
													</TableCell>
													<TableCell align="center">
														<BooleanCell value={item.clientPaymentCoversFlightCosts} />
													</TableCell>
												</>
											)}
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</TableContainer>

					{/* Paginación */}
					{pageTotal > 0 && (
						<Grid container justifyContent="center" flexDirection="row" padding={2}>
							<Grid item xs={12} md={1} textAlign="center">
								<Select
									value={currentSize}
									size="small"
									onChange={(e) => {
										setCurrentSize(Number(e.target.value));
										setCurrentPage(1);
									}}
								>
									{PAGE_SIZE_OPTIONS.map((v) => (
										<MenuItem key={v} value={v}>
											{v}
										</MenuItem>
									))}
								</Select>
							</Grid>
							<Grid item xs={12} md={6} marginTop={1}>
								<Pagination
									count={pageTotal}
									page={currentPage}
									onChange={(_, page) => setCurrentPage(page)}
									color="primary"
								/>
							</Grid>
						</Grid>
					)}
				</>
			)}
		</Box>
	);
};
